/**
 * Cross-platform time-zone helpers used by `TimeInput` and other
 * library components that need to convert wall-clock times to UTC ms
 * without depending on app-level modules. Promoted from
 * `apps/mobile/src/lib/timezone.ts` per ADR-0076 — the apps/mobile
 * module now re-exports `wallTimeToUtcMs` from this file so existing
 * call sites stay valid.
 */

/**
 * Convert a wall-clock time in a given IANA tz to absolute UTC ms.
 *
 * The standard "ambiguity-aware" trick: take the same numeric date in
 * UTC, ask Intl what wall-clock time that maps to in the target tz,
 * and use the difference as the offset. Works across DST transitions.
 *
 * Returns `null` when the inputs don't parse — keeps the rest of the
 * timeline rendering even if a single journey has bad data.
 */
export function wallTimeToUtcMs(localIso: string, timezone: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(localIso);
  if (!m) return null;
  const [, y, mo, d, hh, mm, ss] = m;
  // Pretend the wall time is UTC — gives us a Date pinned to the
  // numeric calendar / clock values, regardless of host tz.
  const asUtc = Date.UTC(
    Number(y), Number(mo) - 1, Number(d),
    Number(hh), Number(mm), Number(ss ?? '0'),
  );
  if (Number.isNaN(asUtc)) return null;

  // Format that UTC instant in the target tz, then re-parse the
  // formatted wall time as UTC. The difference between the two is
  // the tz offset for that wall time.
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(new Date(asUtc));
  } catch {
    // Bad timezone string — caller falls back to floating-time.
    return null;
  }
  const get = (k: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === k)?.value ?? '00';
  // Intl returns hour '24' for midnight in some zones (en-US locale
  // quirk). Normalise to 0-23 before re-assembling.
  const hour = get('hour') === '24' ? '00' : get('hour');
  const tzWallUtc = Date.UTC(
    Number(get('year')), Number(get('month')) - 1, Number(get('day')),
    Number(hour), Number(get('minute')), Number(get('second')),
  );
  const offsetMs = tzWallUtc - asUtc;
  // The wall time the user typed lives in `timezone`, so the true UTC
  // is the as-UTC value minus the offset.
  return asUtc - offsetMs;
}
